import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faPlus } from '@fortawesome/free-solid-svg-icons';
import Button from '../Button/Button';
import ToolTip from '../ToolTip/ToolTip';
import IncomeUnitType from '../../../types/unit/IncomeUnit';
import ApplyToAll, { ApplyColumnInfo } from '../../../utils/ApplyToAll/ApplyToAll';
import { ColumnInfo } from '../../../utils/DataTable/ColumnInfo';
import GenericFilter, { Filter } from '../../../utils/DataTable/GenericFilter/GenericFilter';
import { useReadOnly } from '../../../utils/ReadOnlyContext';

interface UnitTableControlsProps {
    isInternal?: boolean;
    showTableDataControls?: boolean;
    showExportCSVOption?: boolean;
    onCreateUnit: () => void;
    onDeleteAllUnits?: () => void;
    onClearMarketRent?: () => void;
    filterColumns?: ColumnInfo[];
    filters?: Filter<IncomeUnitType>[];
    setFilters?: React.Dispatch<React.SetStateAction<Filter<IncomeUnitType>[]>>;
    applyToAllColumns?: ApplyColumnInfo[];
    handleExportCSV?: () => void;
    handleApplyToAll: (key: keyof IncomeUnitType, value: number | string | null | undefined | boolean) => void
    guidedWizardButton?: JSX.Element;
    children?: React.ReactNode;
}

export function UnitTableControls({
    isInternal = false,
    showTableDataControls = false,
    showExportCSVOption = false,
    onCreateUnit,
    onDeleteAllUnits,
    onClearMarketRent,
    filterColumns,
    filters,
    setFilters,
    applyToAllColumns,
    handleApplyToAll,
    handleExportCSV,
    guidedWizardButton,
    children,
}: UnitTableControlsProps) {
    const { isReadOnly } = useReadOnly()
    const { t } = useTranslation(["unit_table", "common"])

    const renderAdminControls = () => {
        if (!showTableDataControls) return null

        return (
            <>
                {onDeleteAllUnits && (
                    <ToolTip contents={<Button type="danger" onClick={onDeleteAllUnits}>Delete All Units</Button>}>
                        {t("unit-table.delete-all-units-tooltip")}
                    </ToolTip>
                )}

                {onClearMarketRent && (
                    <ToolTip contents={<Button type="danger" onClick={onClearMarketRent}>Clear Market Rent</Button>}>
                        {t("unit-table.clear-market-rent-tooltip")}
                    </ToolTip>
                )}
            </>
        )
    }

    const renderApplyToAllControl = () => {
        if (applyToAllColumns && applyToAllColumns.length > 0 && handleApplyToAll) {
            return (
                <ApplyToAll<IncomeUnitType>
                    columns={applyToAllColumns}
                    handleApplyToAll={handleApplyToAll}
                />
            )
        }
        return null
    }

    const renderFilterControl = () => {
        if (filterColumns && filterColumns.length > 0 && filters !== undefined && setFilters) {
            return (
                <GenericFilter<IncomeUnitType>
                    columns={filterColumns}
                    filters={filters}
                    setFilters={setFilters}
                />
            )
        }
        return null
    }

    const renderExportCSVControl = () => {
        return (<ToolTip contents={
            <Button type='submit'
                text={<FontAwesomeIcon icon={faDownload} />}
                onClick={handleExportCSV}
                ariaLabel='exportCSV'
                className='internal-header'>
            </Button>
        }>{t("unit-table.export-csv-tooltip")}</ToolTip>)
    }

    const addUnitButton = !isReadOnly ?
        (
            <ToolTip contents={
                <Button type='submit'
                    onClick={onCreateUnit}
                    text={isInternal ? <FontAwesomeIcon icon={faPlus} /> : t("unit-table.add")}
                    ariaLabel="addUnit">
                </Button>
            }>
                {t("unit-table.add-unit-tooltip")}
            </ToolTip>
        )
        :
        null

    return (
        <div className={`suite-breakdown-header-row ${isInternal ? 'internal-table' : ''}`}>
            <div className='suite-control-group'>
                {children}
                <div className="unit-table-control-group">
                    {(isInternal && !isReadOnly) && renderApplyToAllControl()}
                    {(isInternal && showExportCSVOption) && renderExportCSVControl()}
                    {renderAdminControls()}
                    {guidedWizardButton}
                    {addUnitButton}
                    {renderFilterControl()}
                </div>
            </div>
        </div>
    )
}